import { useEffect, useState, useCallback, useRef } from 'react';
import { User, AuthChangeEvent, Session } from '@supabase/supabase-js';
import { Database } from '../types/database';
import { supabase } from '../lib/supabase';

type Profile = Database['public']['Tables']['profiles']['Row'];

interface AuthState {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  error: Error | null;
  isFetchingProfile: boolean;
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    profile: null,
    loading: true,
    error: null,
    isFetchingProfile: false,
  });

  // Use refs to track state for stable access in callbacks/effects
  const authStateRef = useRef(authState);
  useEffect(() => {
    authStateRef.current = authState;
  }, [authState]);

  const fetchProfile = useCallback(async (userId: string) => {
    const current = authStateRef.current;
    if (current.isFetchingProfile || (current.profile?.user_id === userId && current.profile !== null)) {
      console.log('[useAuth] fetchProfile: Skipped, already fetching or profile exists for user:', userId);
      if (current.user && current.profile?.user_id === userId) {
        setAuthState(prev => ({ ...prev, loading: false, isFetchingProfile: false }));
      }
      return;
    }

    console.log('[useAuth] fetchProfile: Starting for user:', userId);
    setAuthState(prev => ({ ...prev, isFetchingProfile: true, loading: true }));
    try {
      const { data, error, status } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && status !== 406) {
        console.error('[useAuth] fetchProfile: Error fetching profile:', error);
        setAuthState(prev => ({ ...prev, profile: null, isFetchingProfile: false, loading: false, error }));
        return;
      }

      if (data) {
        console.log('[useAuth] fetchProfile: Profile fetched successfully:', data.email);
        setAuthState(prev => ({
          ...prev,
          profile: data as Profile,
          isFetchingProfile: false,
          loading: false,
          error: null,
        }));
      } else {
        console.log('[useAuth] fetchProfile: No profile found for user_id:', userId);
        setAuthState(prev => ({
          ...prev,
          profile: null,
          isFetchingProfile: false,
          loading: false,
        }));
      }
    } catch (error) {
      console.error('[useAuth] fetchProfile: Error in catch block:', error);
      setAuthState(prev => ({
        ...prev,
        profile: null,
        isFetchingProfile: false,
        loading: false,
        error: error as Error,
      }));
    }
  }, []);

  useEffect(() => {
    console.log('[useAuth] useEffect: Setting up auth listener.');
    let isMounted = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event: AuthChangeEvent, session: Session | null) => {
        if (!isMounted) {
          console.log('[useAuth] onAuthStateChange: Unmounted, skipping event:', event);
          return;
        }
        console.log('[useAuth] onAuthStateChange: Event:', event, 'Session user:', session?.user?.email);

        switch (event) {
          case 'INITIAL_SESSION':
          case 'SIGNED_IN':
            if (session?.user) {
              const current = authStateRef.current;
              const userChanged = current.user?.id !== session.user.id;
              setAuthState(prev => ({ ...prev, user: session.user, loading: true }));
              if (userChanged || !current.profile || current.profile.user_id !== session.user.id) {
                await fetchProfile(session.user.id);
              } else {
                setAuthState(prev => ({ ...prev, loading: false }));
              }
            } else {
              setAuthState({ user: null, profile: null, loading: false, error: null, isFetchingProfile: false });
            }
            break;
          case 'TOKEN_REFRESHED':
            if (session?.user) {
              const current = authStateRef.current;
              if (current.user?.id !== session.user.id) {
                 setAuthState(prev => ({ ...prev, user: session.user, loading: true }));
                 await fetchProfile(session.user.id);
              } else {
                 setAuthState(prev => ({ ...prev, user: session.user, loading: false }));
              }
            } else {
              setAuthState({ user: null, profile: null, loading: false, error: null, isFetchingProfile: false });
            }
            break;
          case 'SIGNED_OUT':
            setAuthState({ user: null, profile: null, loading: false, error: null, isFetchingProfile: false });
            break;
          case 'USER_UPDATED':
            if (session?.user) {
              setAuthState(prev => ({ ...prev, user: session.user, loading: true }));
              await fetchProfile(session.user.id);
            }
            break;
          default:
            if (authStateRef.current.loading && !session?.user) {
                 setAuthState(prev => ({ ...prev, loading: false, isFetchingProfile: false }));
            }
            console.warn('[useAuth] Unhandled auth event:', event);
            break;
        }
      }
    );

    return () => {
      console.log('[useAuth] useEffect: Cleaning up auth listener.');
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  return {
    ...authState,
  };
} 